import { Link, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/layout/DashboardShell";
import { ScanLine, LogIn, LogOut, FileText, PlusCircle, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import QRScannerDialog from "@/components/QRScannerDialog";
import HelpVideoButton from "@/components/HelpVideoButton";

export default function HelperDashboard() {
  const { t } = useT();
  const nav = useNavigate();
  const [counts, setCounts] = useState({ animals: 0, cash: 0, receipts: 0 });
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/helper/animals"), api.get("/helper/cash")]).then(([a, c]) => {
      const today = new Date().toISOString().slice(0, 10);
      const todayCash = c.data.filter((x) => (x.created_at || "").startsWith(today));
      setCounts({
        animals: a.data.length,
        cash: todayCash.reduce((s, x) => s + (x.amount || 0), 0),
        receipts: todayCash.length,
      });
    }).catch(() => {});
  }, []);

  const ACTIONS = [
    { key: "new", label: t("nav.newRegistration"), icon: PlusCircle, primary: true, onClick: () => nav("/helper/new-registration"), testId: "helper-action-new-registration" },
    { key: "gate-in", label: "Gate Entry", icon: LogIn, onClick: () => setScanOpen(true), testId: "helper-action-gate-in" },
    { key: "gate-out", label: "Gate Exit", icon: LogOut, onClick: () => setScanOpen(true), testId: "helper-action-gate-out" },
    { key: "reports", label: t("nav.reports"), icon: FileText, onClick: () => nav("/helper/reports"), testId: "helper-action-reports" },
  ];

  return (
    <DashboardShell
      title={t("nav.dashboard")}
      subtitle={t("helper.tip").slice(0, 60) + "…"}
      action={<HelpVideoButton screen="getting_started" />}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="am-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t("helper.todayCollection")}</div>
          <div className="font-display text-3xl font-extrabold mt-2">₹ {counts.cash.toLocaleString("en-IN")}</div>
          <div className="text-xs text-muted-foreground mt-1">{counts.receipts} {t("helper.receipts")}</div>
        </div>
        <div className="am-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t("helper.animalsRegistered")}</div>
          <div className="font-display text-3xl font-extrabold mt-2">{counts.animals}</div>
        </div>
        <div className="am-card p-6 bg-primary text-primary-foreground border-primary">
          <div className="text-xs uppercase tracking-widest font-bold opacity-80">{t("helper.helperTip")}</div>
          <div className="mt-2 text-sm leading-relaxed">{t("helper.tip")}</div>
        </div>
      </div>

      {/* PRIMARY ACTION — huge tap target */}
      <Link
        to="/helper/new-registration"
        data-testid="helper-primary-cta"
        className="am-card p-8 mb-4 flex items-center gap-5 bg-primary text-primary-foreground border-primary hover:opacity-95"
      >
        <div className="w-16 h-16 rounded-2xl bg-primary-foreground/15 grid place-items-center">
          <PlusCircle className="w-9 h-9" />
        </div>
        <div className="flex-1">
          <div className="font-display font-extrabold text-2xl">{t("nav.newRegistration")}</div>
          <div className="text-primary-foreground/80 text-sm mt-1">Seller + Animal + Payment + Gate Pass in one flow — under a minute.</div>
        </div>
        <ArrowRight className="w-6 h-6" />
      </Link>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {ACTIONS.slice(1).map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.key}
              data-testid={a.testId}
              onClick={a.onClick}
              className="am-card p-5 flex flex-col items-start gap-3 hover:bg-accent text-left"
            >
              <div className="w-11 h-11 rounded-2xl bg-accent text-primary grid place-items-center">
                <Icon className="w-5 h-5" />
              </div>
              <span className="font-display font-bold text-base">{a.label}</span>
            </button>
          );
        })}
      </div>

      {/* SINGLE scanner entry point — fixed FAB, right-center, all screens */}
      <button
        data-testid="helper-scan-fab"
        onClick={() => setScanOpen(true)}
        className="fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 w-16 h-16 rounded-2xl bg-primary text-primary-foreground shadow-2xl grid place-items-center hover:scale-105 transition-transform z-40"
        aria-label="Scan QR"
      >
        <ScanLine className="w-7 h-7" />
      </button>

      <QRScannerDialog open={scanOpen} onOpenChange={setScanOpen} />
    </DashboardShell>
  );
}
